<template>
  <section
    class="testimonials-section"
    dir="rtl"
    aria-labelledby="testimonials-title"
  >
    <div class="section-container">
      <div class="section-label">{{ config.label || 'آراء الطلاب' }}</div>
      <h2 id="testimonials-title" class="section-title">{{ config.title || 'ماذا يقول طلابنا؟' }}</h2>
    </div>

    <!-- Loading skeletons -->
    <div v-if="loading" class="testimonials-outer testimonials-outer--loading" aria-busy="true">
      <div class="testimonials-skeletons" aria-hidden="true">
        <div class="tcard tcard--skeleton" v-for="n in 4" :key="'sk-' + n">
          <div class="tsk tsk-stars"></div>
          <div class="tsk tsk-line"></div>
          <div class="tsk tsk-line tsk-line--short"></div>
          <div class="tcard-author tcard-author--skeleton">
            <div class="tsk tsk-avatar"></div>
            <div class="tcard-author-copy"><div class="tsk tsk-name"></div></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="!testimonials.length" class="testimonials-empty" role="status">
      <div class="te-icon" aria-hidden="true">💬</div>
      <p>لا توجد تقييمات بعد — كن أول من يشارك رأيه!</p>
    </div>

    <!-- Premium Swiper slider -->
    <div v-else class="testimonials-outer testimonials-outer--slider">
      <Swiper
        class="testimonials-swiper"
        dir="rtl"
        :modules="swiperModules"
        :slides-per-view="1"
        :space-between="12"
        :speed="700"
        :loop="canLoopAt(1)"
        :rewind="shouldRewindAt(1)"
        :breakpoints="sliderBreakpoints"
        :grab-cursor="hasMultipleTestimonials"
        :allow-touch-move="hasMultipleTestimonials"
        :touch-start-prevent-default="false"
        :keyboard="keyboardOptions"
        :mousewheel="mousewheelOptions"
        :navigation="hasMultipleTestimonials"
        :pagination="paginationOptions"
        :autoplay="autoplayOptions"
        :a11y="a11yOptions"
        :watch-overflow="true"
        :observer="true"
        :observe-slide-children="true"
        :lazy-preload-prev-next="1"
        @swiper="onSwiperReady"
        @slideChange="syncFocusedSlide"
        @breakpoint="syncFocusedSlide"
        @resize="handleSwiperResize"
      >
        <SwiperSlide
          v-for="(item, idx) in testimonials"
          :key="testimonialKey(item, idx)"
          class="testimonial-slide"
          :class="{ 'testimonial-slide--focused': idx === focusedSlideIndex }"
        >
          <div class="testimonial-slide__surface">
            <article
              class="tcard"
              :aria-current="idx === focusedSlideIndex ? 'true' : undefined"
              :aria-label="`رأي الطالب ${item.student_name || ''}`"
            >
              <div
                class="tcard-stars"
                role="img"
                :aria-label="`تقييم ${normalizedRating(item.number_of_stars)} من 5 نجوم`"
              >
                <span
                  v-for="star in 5"
                  :key="star"
                  class="tcard-star"
                  :class="star <= normalizedRating(item.number_of_stars) ? 'tcard-star--on' : 'tcard-star--off'"
                  aria-hidden="true"
                >★</span>
              </div>

              <div class="tcard-review">
                <p
                  :ref="(element) => setReviewTextRef(reviewKey(item, idx), element)"
                  class="tcard-text"
                  :class="{ 'tcard-text--expanded': isExpanded(reviewKey(item, idx)) }"
                >"{{ item.note }}"</p>
                <button
                  v-if="isLongReview(reviewKey(item, idx))"
                  type="button"
                  class="tcard-more swiper-no-swiping"
                  :aria-expanded="isExpanded(reviewKey(item, idx))"
                  @click.stop="toggleReview(reviewKey(item, idx))"
                >
                  {{ isExpanded(reviewKey(item, idx)) ? 'عرض أقل' : 'عرض المزيد' }}
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path d="m5.75 7.5 4.25 4.25 4.25-4.25" />
                  </svg>
                </button>
              </div>

              <div class="tcard-author">
                <div class="tcard-avatar" aria-hidden="true">
                  <img
                    v-if="avatarUrl(item) && !hasAvatarError(reviewKey(item, idx))"
                    :src="avatarUrl(item)"
                    :alt="item.student_name || 'صورة الطالب'"
                    loading="lazy"
                    decoding="async"
                    @error="markAvatarError(reviewKey(item, idx))"
                  />
                  <span v-else>{{ getInitial(item.student_name) }}</span>
                </div>
                <div class="tcard-author-copy">
                  <div class="tcard-name">{{ item.student_name }}</div>
                  <div v-if="item.academic_year" class="tcard-year">{{ item.academic_year }}</div>
                </div>
              </div>
            </article>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { A11y, Autoplay, Keyboard, Mousewheel, Navigation, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const props = defineProps({
  testimonials: { type: Array, default: () => [] },
  loading: { type: Boolean, default: true },
  config: { type: Object, default: () => ({}) },
});

const swiperModules = [A11y, Autoplay, Keyboard, Mousewheel, Navigation, Pagination];
const focusedSlideIndex = ref(0);
const expandedReviews = ref(new Set());
const longReviews = ref(new Set());
const avatarErrors = ref(new Set());
const reviewTextElements = new Map();
let swiperInstance = null;

const hasMultipleTestimonials = computed(() => props.testimonials.length > 1);

// Swiper loop mode needs at least one more slide than slidesPerView. When a
// breakpoint temporarily has too few cards, rewind provides the same seamless
// navigation behavior without triggering Swiper's loop warning.
function canLoopAt(slidesPerView) {
  return props.testimonials.length > slidesPerView;
}

function shouldRewindAt(slidesPerView) {
  return hasMultipleTestimonials.value && !canLoopAt(slidesPerView);
}

const sliderBreakpoints = computed(() => ({
  768: {
    slidesPerView: 2,
    spaceBetween: 16,
    loop: canLoopAt(2),
    rewind: shouldRewindAt(2),
  },
  1024: {
    slidesPerView: 3,
    spaceBetween: 20,
    loop: canLoopAt(3),
    rewind: shouldRewindAt(3),
  },
  1400: {
    slidesPerView: 4,
    spaceBetween: 24,
    loop: canLoopAt(4),
    rewind: shouldRewindAt(4),
  },
}));

const autoplayOptions = computed(() => (hasMultipleTestimonials.value ? {
  delay: 5000,
  disableOnInteraction: false,
  pauseOnMouseEnter: true,
  waitForTransition: true,
} : false));

const keyboardOptions = computed(() => ({
  enabled: hasMultipleTestimonials.value,
  onlyInViewport: true,
  pageUpDown: false,
  speed: 700,
}));

const mousewheelOptions = computed(() => ({
  enabled: hasMultipleTestimonials.value,
  forceToAxis: false,
  releaseOnEdges: true,
  sensitivity: 0.7,
  thresholdDelta: 30,
  thresholdTime: 350,
}));

const paginationOptions = computed(() => (hasMultipleTestimonials.value ? {
  clickable: true,
  bulletElement: 'button',
  dynamicBullets: props.testimonials.length > 7,
  dynamicMainBullets: 5,
} : false));

const a11yOptions = {
  enabled: true,
  containerMessage: 'آراء طلاب أستاذ طارق',
  containerRoleDescriptionMessage: 'عارض شرائح',
  itemRoleDescriptionMessage: 'رأي طالب',
  prevSlideMessage: 'الرأي السابق',
  nextSlideMessage: 'الرأي التالي',
  firstSlideMessage: 'هذا هو الرأي الأول',
  lastSlideMessage: 'هذا هو الرأي الأخير',
  paginationBulletMessage: 'الانتقال إلى الرأي رقم {{index}}',
};

function testimonialKey(item, index) {
  return item?.id != null ? `testimonial-${item.id}` : `testimonial-${index}`;
}

function reviewKey(item, index) {
  return testimonialKey(item, index);
}

function normalizedRating(rating) {
  const numericRating = Number(rating);
  return Number.isFinite(numericRating) ? Math.min(5, Math.max(0, numericRating)) : 0;
}

function getInitial(name) {
  const normalizedName = String(name || '').trim();
  return normalizedName ? normalizedName.charAt(0) : '؟';
}

function avatarUrl(item) {
  const candidates = [
    item?.student_avatar_link,
    item?.student_avatar_url,
    item?.avatar_link,
    item?.avatar_url,
    item?.profile_image_url,
    item?.profile_photo_url,
  ];
  return candidates.find((candidate) => typeof candidate === 'string' && candidate.trim()) || '';
}

function hasAvatarError(key) {
  return avatarErrors.value.has(key);
}

function markAvatarError(key) {
  const nextErrors = new Set(avatarErrors.value);
  nextErrors.add(key);
  avatarErrors.value = nextErrors;
}

function setReviewTextRef(key, element) {
  if (element) reviewTextElements.set(key, element);
  else reviewTextElements.delete(key);
}

function isExpanded(key) {
  return expandedReviews.value.has(key);
}

function isLongReview(key) {
  return longReviews.value.has(key);
}

function measureLongReviews() {
  const nextLongReviews = new Set();

  reviewTextElements.forEach((element, key) => {
    if (expandedReviews.value.has(key) || element.scrollHeight > element.clientHeight + 1) {
      nextLongReviews.add(key);
    }
  });

  longReviews.value = nextLongReviews;
}

function toggleReview(key) {
  const nextExpandedReviews = new Set(expandedReviews.value);
  if (nextExpandedReviews.has(key)) nextExpandedReviews.delete(key);
  else nextExpandedReviews.add(key);
  expandedReviews.value = nextExpandedReviews;

  nextTick(() => {
    swiperInstance?.update();
    measureLongReviews();
  });
}

function syncFocusedSlide(swiper = swiperInstance) {
  const testimonialCount = props.testimonials.length;
  if (!testimonialCount) return;

  const configuredSlidesPerView = Number(swiper?.params?.slidesPerView) || 1;
  const visibleSlides = Math.min(configuredSlidesPerView, testimonialCount);
  const centerOffset = Math.floor((visibleSlides - 1) / 2);
  const firstVisibleIndex = Number(swiper?.realIndex) || 0;
  focusedSlideIndex.value = (firstVisibleIndex + centerOffset) % testimonialCount;
}

function onSwiperReady(swiper) {
  swiperInstance = swiper;
  syncFocusedSlide(swiper);
  nextTick(measureLongReviews);
}

function handleSwiperResize(swiper) {
  syncFocusedSlide(swiper);
  nextTick(measureLongReviews);
}

function handleWindowResize() {
  nextTick(measureLongReviews);
}

watch(
  () => props.testimonials,
  () => {
    expandedReviews.value = new Set();
    longReviews.value = new Set();
    avatarErrors.value = new Set();

    nextTick(() => {
      swiperInstance?.update();
      syncFocusedSlide();
      measureLongReviews();
    });
  },
  { deep: true },
);

onMounted(() => {
  window.addEventListener('resize', handleWindowResize, { passive: true });
  document.fonts?.ready.then(() => nextTick(measureLongReviews));
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleWindowResize);
  reviewTextElements.clear();
  swiperInstance = null;
});
</script>

<style scoped>
*, *::before, *::after { box-sizing:border-box;margin:0;padding:0; }
.section-container { max-width:1200px;margin:0 auto;padding:0 2rem; }
.section-label { display:inline-block;background:rgba(212,175,55,.08);color:#D4AF37;border:1px solid rgba(212,175,55,.2);border-radius:99px;padding:.3rem 1rem;font-size:.8rem;font-weight:700;margin-bottom:1rem; }
.section-title { font-size:clamp(1.6rem,3vw,2.2rem);font-weight:800;color:#F0EDE4;margin-bottom:2.5rem;line-height:1.3; }

.testimonials-section { padding:6rem 0;background:#0A0A0A;overflow:hidden;direction:rtl; }
.testimonials-section .section-container { margin-bottom:3rem; }

.testimonials-outer { width:100%;margin:0 auto; }
.testimonials-outer--slider { max-width:1500px;padding:0 1.25rem; }
.testimonials-swiper {
  --swiper-theme-color:#D4AF37;
  --swiper-navigation-size:44px;
  --swiper-pagination-bullet-size:9px;
  --swiper-pagination-bullet-horizontal-gap:5px;
  overflow:hidden;
  padding:1.35rem .7rem 4.75rem;
}

:deep(.swiper-wrapper) { align-items:stretch; }
.testimonial-slide { height:auto;display:flex;padding:.75rem; }
.testimonial-slide__surface {
  width:100%;height:100%;display:flex;
  opacity:.76;
  transform:scale(.95);
  transform-origin:center;
  transition:transform .7s cubic-bezier(.22,1,.36,1),opacity .55s ease;
  will-change:transform,opacity;
}
.testimonial-slide--focused .testimonial-slide__surface {
  opacity:1;
  transform:translateY(-5px) scale(1.05);
}

.tcard {
  background:#1A1A1A;border-radius:20px;padding:1.75rem;width:100%;min-width:0;height:100%;min-height:250px;
  box-shadow:0 6px 24px rgba(0,0,0,.4);border:1px solid rgba(212,175,55,.12);
  display:flex;flex-direction:column;gap:.85rem;
  transition:transform .35s cubic-bezier(.22,1,.36,1),box-shadow .55s ease,border-color .55s ease;
}
.testimonial-slide:not(.testimonial-slide--focused) .tcard { box-shadow:0 4px 16px rgba(0,0,0,.35); }
.testimonial-slide--focused .tcard { border-color:rgba(212,175,55,.16);box-shadow:0 22px 55px rgba(212,175,55,.17),0 8px 22px rgba(17,24,39,.08); }
.tcard:hover { transform:translateY(-4px);box-shadow:0 20px 50px rgba(212,175,55,.12); }
.testimonial-slide--focused .tcard:hover { box-shadow:0 26px 62px rgba(212,175,55,.2),0 10px 24px rgba(17,24,39,.09); }

.tcard-stars { display:flex;gap:.1rem;font-size:.9rem;letter-spacing:.1rem; }
.tcard-star { font-size:1.1rem;transition:transform .2s ease; }
.tcard-star--on { color:#F59E0B; }
.tcard-star--off { color:#E5E7EB; }
.testimonial-slide--focused .tcard-star--on { animation:starGlow .65s cubic-bezier(.22,1,.36,1) both; }
.testimonial-slide--focused .tcard-star--on:nth-child(2) { animation-delay:.04s; }
.testimonial-slide--focused .tcard-star--on:nth-child(3) { animation-delay:.08s; }
.testimonial-slide--focused .tcard-star--on:nth-child(4) { animation-delay:.12s; }
.testimonial-slide--focused .tcard-star--on:nth-child(5) { animation-delay:.16s; }

.tcard-review { flex:1;display:flex;flex-direction:column;align-items:flex-start; }
.tcard-text {
  width:100%;font-size:.9rem;color:rgba(240,237,228,.75);line-height:1.7;overflow:hidden;
  display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:3;line-clamp:3;
}
.tcard-text--expanded { display:block;overflow:visible;-webkit-line-clamp:unset;line-clamp:unset; }
.tcard-more {
  display:inline-flex;align-items:center;gap:.2rem;margin-top:.45rem;border:0;background:transparent;
  color:#D4AF37;font-family:inherit;font-size:.78rem;font-weight:700;line-height:1.5;cursor:pointer;
  transition:color .2s ease,transform .2s ease;
}
.tcard-more:hover { color:#C89B2D;transform:translateY(-1px); }
.tcard-more:focus-visible { outline:2px solid rgba(212,175,55,.45);outline-offset:3px;border-radius:5px; }
.tcard-more svg { width:14px;height:14px;fill:none;stroke:currentColor;stroke-width:1.8;transition:transform .3s ease; }
.tcard-more[aria-expanded="true"] svg { transform:rotate(180deg); }

.tcard-author { display:flex;align-items:center;gap:.75rem;margin-top:.25rem; }
.tcard-author--skeleton { margin-top:auto; }
.tcard-author-copy { flex:1;min-width:0; }
.tcard-avatar {
  width:40px;height:40px;border-radius:50%;overflow:hidden;
  background:linear-gradient(135deg,#D4AF37,#C89B2D);display:flex;align-items:center;justify-content:center;
  color:#fff;font-weight:700;font-size:1rem;flex-shrink:0;
  box-shadow:0 5px 14px rgba(212,175,55,.2);
}
.tcard-avatar img { width:100%;height:100%;object-fit:cover; }
.tcard-name { overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:700;color:#F0EDE4;font-size:.9rem; }
.tcard-year { overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:rgba(240,237,228,.5);font-size:.78rem;font-weight:600;margin-top:.1rem; }

:deep(.swiper-button-prev),
:deep(.swiper-button-next) {
  top:auto;bottom:.5rem;margin-top:0;width:44px;height:44px;border-radius:50%;
  border:1px solid rgba(212,175,55,.16);background:#1A1A1A;color:#D4AF37;
  box-shadow:0 8px 22px rgba(0,0,0,.35);
  transition:transform .25s ease,box-shadow .25s ease,background .25s ease,color .25s ease;
}
:deep(.swiper-button-prev) { right:.35rem;left:auto; }
:deep(.swiper-button-next) { left:.35rem;right:auto; }
:deep(.swiper-button-prev:hover),
:deep(.swiper-button-next:hover) { transform:translateY(-2px);background:#D4AF37;color:#0F0F0F;box-shadow:0 12px 26px rgba(212,175,55,.25); }
:deep(.swiper-button-prev:focus-visible),
:deep(.swiper-button-next:focus-visible) { outline:3px solid rgba(212,175,55,.28);outline-offset:3px; }
:deep(.swiper-button-prev svg),
:deep(.swiper-button-next svg) { width:18px;height:18px; }
:deep(.swiper-button-lock),
:deep(.swiper-navigation-disabled) { display:none !important; }

:deep(.swiper-pagination) { bottom:1.55rem !important;line-height:0; }
:deep(.swiper-pagination-bullet) {
  position:relative;width:18px;height:44px;border:0;background:transparent;opacity:1;appearance:none;
  transition:transform .35s ease;
}
:deep(.swiper-pagination-bullet)::after {
  content:"";position:absolute;top:50%;left:50%;width:9px;height:9px;border-radius:99px;
  background:rgba(240,237,228,.35);opacity:.7;transform:translate(-50%,-50%);
  transition:width .35s ease,opacity .35s ease,background .35s ease;
}
:deep(.swiper-pagination-bullet:hover)::after { opacity:.8; }
:deep(.swiper-pagination-bullet:focus-visible) { outline:2px solid rgba(212,175,55,.45);outline-offset:3px; }
:deep(.swiper-pagination-bullet-active)::after { width:28px;background:#D4AF37;opacity:1; }
:deep(.swiper-pagination-lock),
:deep(.swiper-pagination-disabled) { display:none !important; }

/* Loading skeletons */
.testimonials-outer--loading { max-width:1500px;padding:1.35rem 2rem;overflow:hidden; }
.testimonials-skeletons { display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:1.5rem; }
.tcard--skeleton { pointer-events:none;min-height:250px; }
.tsk { background:linear-gradient(90deg,#1E1E1E 25%,#2A2A2A 50%,#1E1E1E 75%);background-size:200% 100%;animation:shimmer 1.4s infinite;border-radius:8px; }
.tsk-stars { height:16px;width:80px;border-radius:99px; }
.tsk-line { height:13px;width:95%;margin-top:.5rem; }
.tsk-line--short { width:65%; }
.tsk-avatar { width:40px;height:40px;border-radius:50%;flex-shrink:0; }
.tsk-name { height:14px;width:100px; }

/* Empty state */
.testimonials-empty { max-width:620px;margin:0 auto;text-align:center;padding:3rem 1.5rem;border:1px dashed rgba(212,175,55,.25);border-radius:20px;background:rgba(255,255,255,.03); }
.te-icon { font-size:3rem;margin-bottom:.75rem;filter:drop-shadow(0 7px 14px rgba(212,175,55,.12)); }
.testimonials-empty p { color:rgba(240,237,228,.55);font-size:.95rem; }

@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
@keyframes starGlow { 0%{transform:scale(.78);opacity:.55} 100%{transform:scale(1);opacity:1} }

@media (max-width:1399px) {
  .testimonials-skeletons { grid-template-columns:repeat(3,minmax(0,1fr)); }
  .testimonials-skeletons .tcard--skeleton:nth-child(4) { display:none; }
}

@media (max-width:1023px) {
  .testimonials-skeletons { grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem; }
  .testimonials-skeletons .tcard--skeleton:nth-child(3) { display:none; }
}

@media (max-width:767px) {
  /* Mobile app home: compact section + heading (was 6rem band + large title). */
  .testimonials-section { padding:1.25rem 0 .25rem;background:transparent; }
  .section-container { padding:0 1rem; }
  .section-label { font-size:.7rem;padding:.22rem .75rem;margin-bottom:.55rem; }
  .section-title { font-size:1.15rem;margin-bottom:0; }
  .testimonials-section .section-container { margin-bottom:.5rem; }
  .testimonials-outer--slider { padding:0 .35rem; }
  .testimonials-swiper { padding-top:.9rem;padding-inline:.2rem;padding-bottom:3.6rem; }
  .testimonial-slide { padding:.6rem .5rem; }
  .tcard { padding:1.25rem;min-height:200px; }
  .testimonials-outer--loading { padding:1.1rem 1.35rem; }
  .testimonials-skeletons { grid-template-columns:1fr; }
  .testimonials-skeletons .tcard--skeleton:nth-child(n+2) { display:none; }
  :deep(.swiper-button-prev) { right:1rem; }
  :deep(.swiper-button-next) { left:1rem; }
}

@media (prefers-reduced-motion:reduce) {
  .testimonial-slide__surface,
  .tcard,
  .tcard-star,
  .tcard-more,
  .tcard-more svg,
  :deep(.swiper-button-prev),
  :deep(.swiper-button-next),
  :deep(.swiper-pagination-bullet) { transition-duration:.01ms !important;animation-duration:.01ms !important; }
}
</style>
